interface TileFailureInfo {
  failureCount: number;
  lastFailureTime: number;
  backoffUntil: number;
}
interface TerminalFailureInfo {
  reason: string;
  firstAt: number;
  ttlMs: number;
}

/**
 * Manages tile request retries and cancellation logic.
 * Keeps track of which tiles are currently needed and handles failure backoff.
 */
export class WMTSRequestManager {
  private tileFailures = new Map<string, TileFailureInfo>();
  private terminalFailures = new Map<string, TerminalFailureInfo>();
  private activeViewportTiles = new Set<string>();
  private readonly maxRetries = 3;
  private readonly baseBackoffMs = 1000;
  private readonly maxBackoffMs = 6000; // TODO: May be way too high, adjust as needed

  /**
   * Check if a tile should be retried based on failure history
   */
  shouldRetryTile(tileKey: string): boolean {
    const failure = this.tileFailures.get(tileKey);
    if (!failure) {
      return true;
    }

    if (failure.failureCount >= this.maxRetries) {
      return false;
    }

    // Still in backoff period, don't retry yet
    if (Date.now() < failure.backoffUntil) {
      return false;
    }
    if (failure.failureCount > 0) {
      console.warn(`Tile ${tileKey} retrying after ${failure.failureCount} failures`);
    }
    return true;
  }

  /**
   * Record a failure for retry logic
   */
  recordFailure(tileKey: string, error: Error): void {
    // Don't record aborted requests as failures
    if (error.name === "AbortError") {
      return;
    }

    const existing = this.tileFailures.get(tileKey) || {
      failureCount: 0,
      lastFailureTime: 0,
      backoffUntil: 0,
    };

    existing.failureCount++;
    existing.lastFailureTime = Date.now();
    existing.backoffUntil = this.calculateBackoff(existing.failureCount);
    this.tileFailures.set(tileKey, existing);

    const waitMs = Math.max(0, existing.backoffUntil - Date.now());
    console.warn(`Tile ${tileKey} failed (${existing.failureCount}/${this.maxRetries}). Next retry in ${waitMs}ms`);
  }

  recordTerminalFailure(tileKey: string, reason: string, ttlMs: number): void {
    this.terminalFailures.set(tileKey, { reason, firstAt: Date.now(), ttlMs });
  }
  isTerminalFailure(tileKey: string): boolean {
    const terminalInfo = this.terminalFailures.get(tileKey);
    if (!terminalInfo) {
      return false;
    }
    const expired = Date.now() - terminalInfo.firstAt > terminalInfo.ttlMs;
    if (expired) {
      this.terminalFailures.delete(tileKey);
      return false;
    }
    return true;
  }

  /**
   * Returns remaining TTL in milliseconds for a terminal failure, or null if none.
   */
  getTerminalTTLmsRemaining(tileKey: string): number | null {
    const terminalInfo = this.terminalFailures.get(tileKey);
    if (!terminalInfo) {
      return null;
    }
    const remaining = terminalInfo.firstAt + terminalInfo.ttlMs - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  classifyAndRecord(error: Error, tileKey: string, status?: number, bodyText?: string, contentType?: string): void {
    const isServiceException =
      /<ServiceException/i.test(bodyText ?? "") || /not available at time/i.test(bodyText ?? "");

    // Simple cadence heuristic; refine per-parameter if needed:
    const guessTTLms = (statusCode?: number) => {
      // Satellite/Radar short, model tiles longer; baseline values:
      if (statusCode === 404 && isServiceException) {
        return 2 * 60_000; // 2 min
      }
      if (statusCode === 400 || statusCode === 403) {
        return 10 * 60_000; // 10 min
      }
      return 2 * 60_000;
    };

    // NON-RETRIABLE: 404 (not available), 400/403 (invalid/forbidden)
    if (status === 404 || status === 400 || status === 403) {
      const ttl = guessTTLms(status);
      this.recordTerminalFailure(tileKey, `HTTP ${status}${isServiceException ? " ServiceException" : ""}`, ttl);
      return;
    }

    // Special case: HTTP 200 but XML/ServiceException (some servers respond like this)
    if (contentType && !contentType.startsWith("image/") && isServiceException) {
      this.recordTerminalFailure(tileKey, "ServiceException 200/XML", 2 * 60_000);
      return;
    }

    // RETRIABLE: 408, 429, 5xx, network errors, etc. -> recordFailure only
    this.recordFailure(tileKey, error);
  }

  /**
   * Clear failure record on successful load
   */
  clearFailure(tileKey: string): void {
    this.tileFailures.delete(tileKey);
    this.terminalFailures.delete(tileKey);
  }

  /**
   * Mark tiles as needed for current viewport (for cancellation logic)
   */
  markTilesAsActive(tileKeys: string[]): void {
    this.activeViewportTiles = new Set(tileKeys);
  }

  /**
   * Check if a tile is still needed (not cancelled)
   */
  isTileNeeded(tileKey: string): boolean {
    return this.activeViewportTiles.has(tileKey);
  }

  /**
   * Get current statistics for debugging (useful for future metrics)
   */
  getStats() {
    const now = Date.now();
    let inBackoff = 0;
    let permanentlyFailed = 0;

    for (const failure of this.tileFailures.values()) {
      if (failure.failureCount >= this.maxRetries) {
        permanentlyFailed++;
      } else if (now < failure.backoffUntil) {
        inBackoff++;
      }
    }

    return {
      activeTiles: this.activeViewportTiles.size,
      totalFailures: this.tileFailures.size,
      tilesInBackoff: inBackoff,
      permanentlyFailedTiles: permanentlyFailed,
      terminalTiles: this.terminalFailures.size,
    };
  }

  /**
   * Get failure info for a specific tile (debugging)
   */
  getFailureInfo(tileKey: string): TileFailureInfo | undefined {
    return this.tileFailures.get(tileKey);
  }

  /**
   * Force clear failure history for a tile (manual retry)
   */
  forceRetry(tileKey: string): boolean {
    return this.tileFailures.delete(tileKey);
  }

  private calculateBackoff(failureCount: number): number {
    const backoffMs = Math.min(this.baseBackoffMs * 2 ** (failureCount - 1), this.maxBackoffMs);
    return Date.now() + backoffMs;
  }
}
